import React, { useState, useEffect, useContext } from "react";
import { LanguageContext } from "../../../../contexts/LanguageContext";
import { translations } from "../../../../translations";

const SpellTheWordDemo = () => {
  const { language } = useContext(LanguageContext);
  const t = translations[language];

  const [letters, setLetters] = useState([]);
  const [filledLetters, setFilledLetters] = useState([]);
  const [isCorrect, setIsCorrect] = useState(null);
  const [animationClass, setAnimationClass] = useState("");

  const demoSentence = t.demo.sentence;
  const wordToSpell = language === "EN" ? "spell" : "épeler";

  const shuffleLettersWithConstraint = (word) => {
    let shuffled;
    do {
      shuffled = word
        .split("")
        .map((letter, index) => ({
          id: `letter-${index}`,
          content: letter,
        }))
        .sort(() => Math.random() - 0.5);
    } while (shuffled.map((l) => l.content).join("") === word);
    return shuffled;
  };

  useEffect(() => {
    const shuffledLetters = shuffleLettersWithConstraint(wordToSpell);
    setLetters(shuffledLetters);
    setFilledLetters(Array(wordToSpell.length).fill(null));
    setIsCorrect(null);
    setAnimationClass("");
  }, [language, wordToSpell]);

  const handleLetterClick = (letter) => {
    const nextEmptyIndex = filledLetters.findIndex((l) => l === null);
    if (nextEmptyIndex !== -1) {
      const newFilledLetters = [...filledLetters];
      newFilledLetters[nextEmptyIndex] = letter;

      const newLetters = letters.filter((l) => l.id !== letter.id);

      setFilledLetters(newFilledLetters);
      setLetters(newLetters);

      if (nextEmptyIndex === wordToSpell.length - 1) {
        const currentSpelling = newFilledLetters
          .map((letter) => letter?.content || "")
          .join("");
        const correct = currentSpelling === wordToSpell;
        setIsCorrect(correct);
        setAnimationClass(correct ? "correct" : "incorrect");

        setTimeout(() => {
          setAnimationClass("");
        }, 1000);
      }
    }
  };

  const handleTryAgain = () => {
    const shuffledLetters = shuffleLettersWithConstraint(wordToSpell);
    setLetters(shuffledLetters);
    setFilledLetters(Array(wordToSpell.length).fill(null));
    setIsCorrect(null);
    setAnimationClass("");
  };

  const renderSentence = () => {
    const parts = demoSentence.split(/(\[.*?\])/g);
    return parts.map((part, partIndex) => {
      if (part.startsWith("[") && part.endsWith("]")) {
        return (
          <span key={partIndex} className="demo-spell-word-blank">
            {Array(wordToSpell.length)
              .fill("_")
              .map((_, index) => (
                <span
                  key={index}
                  className={`demo-letter-slot ${animationClass}`}
                >
                  {filledLetters[index]?.content || "_"}
                </span>
              ))}
          </span>
        );
      }
      return <span key={partIndex}>{part}</span>;
    });
  };

  return (
    <div className="demo-spell-container">
      <h3>{t.demo.title}</h3>
      <div className="demo-sentence-display">{renderSentence()}</div>
      <div className="demo-letters-container">
        {letters.map((letter) => (
          <div
            key={letter.id}
            onClick={() => handleLetterClick(letter)}
            className="demo-letter-piece"
          >
            {letter.content}
          </div>
        ))}
      </div>
      {isCorrect !== null && (
        <p className="demo-spelling-result">
          {isCorrect ? t.demo.correct : t.demo.incorrect}
        </p>
      )}
      <button className="demo-try-again-button" onClick={handleTryAgain}>
        {t.demo.tryAgain}
      </button>
    </div>
  );
};

export default SpellTheWordDemo;
